import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Crown, Edit, ListOrdered, Loader2, Plus, Save, ScrollText, Trash2, Trophy, X, Maximize2, Link as LinkIcon, Shield } from "lucide-react";
import api, { formatApiErrorDetail } from "../api";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import ClanLogo from "../components/clan/ClanLogo";

function LeagueDetailPageLegacy() {
  const QUALIFY_TOP = 4;
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [league, setLeague] = useState(null);
  const [standings, setStandings] = useState([]);
  const [leagueRules, setLeagueRules] = useState([]);
  const [activeTab, setActiveTab] = useState("standings");
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [creatingRule, setCreatingRule] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", order: 1, images: [] });
  
  // حالة رابط الصورة المباشر ونوافذ التكبير
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  const isStaff = user?.role === "admin" || user?.role === "owner";
  const isOwner = user?.role === "owner";

  const loadLeagueDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/leagues/${id}/leaderboard`);
      setLeague(data?.league || null);
      setStandings(Array.isArray(data?.standings) ? data.standings : []);
    } catch (err) {
      setLeague(null);
      setStandings([]);
      setError(formatApiErrorDetail(err?.response?.data?.detail ?? err?.message));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRules = useCallback(async () => {
    if (!id) return;
    setRulesLoading(true);
    try {
      const { data } = await api.get(`/leagues/${id}/rules`);
      const normalized = Array.isArray(data)
        ? data.map((rule) => ({
            ...rule,
            images: Array.isArray(rule?.images)
              ? rule.images.filter((img) => typeof img === "string" && img)
              : rule?.image
                ? [rule.image]
                : [],
          }))
        : [];
      setLeagueRules(normalized);
    } catch {
      setLeagueRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      await loadLeagueDetails();
    })();
  }, [loadLeagueDetails]);

  useEffect(() => {
    if (activeTab !== "rules" || rulesLoaded) return;
    (async () => {
      await loadRules();
      setRulesLoaded(true);
    })();
  }, [activeTab, rulesLoaded, loadRules]);

  const rulesImages = useMemo(
    () => leagueRules.flatMap((rule) => (Array.isArray(rule.images) ? rule.images : [])),
    [leagueRules],
  );

  const startCreateRule = () => {
    setCreatingRule(true);
    setEditingRule(null);
    setImageUrlInput("");
    setForm({ title: "", body: "", order: leagueRules.length + 1, images: [] });
  };

  const startEditRule = (rule) => {
    setCreatingRule(false);
    setEditingRule(rule.id);
    setImageUrlInput("");
    const images = Array.isArray(rule.images)
      ? rule.images
      : rule.image
        ? [rule.image]
        : [];
    setForm({
      title: rule.title || "",
      body: rule.body || "",
      order: Number(rule.order || 1),
      images,
    });
  };

  const cancelRuleForm = () => {
    setCreatingRule(false);
    setEditingRule(null);
    setImageUrlInput("");
    setForm({ title: "", body: "", order: 1, images: [] });
  };

  const addImageUrl = () => {
    const rawUrl = imageUrlInput.trim();
    if (!rawUrl) return;
    setForm((prev) => ({ ...prev, images: [...prev.images, rawUrl] }));
    setImageUrlInput("");
  };

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (file.size > 3_000_000) {
        toast.error(`الصورة ${file.name} كبيرة (الحد 3MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);

        const { data } = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        let uploadedUrl = data?.url || data?.image_url || data?.path;
        if (uploadedUrl) {
          if (uploadedUrl.startsWith("/")) {
            uploadedUrl = `${window.location.origin}${uploadedUrl}`;
          }
          setForm((prev) => ({ ...prev, images: [...prev.images, uploadedUrl] }));
          toast.success(`تم رفع الصورة ${file.name}`);
        } else {
          toast.error("يرجى استخدام خانة إضافة رابط صورة مباشر");
        }
      } catch {
        toast.error("فشل رفع الملف من الجهاز، يرجى استبداله برابط صورة مباشر");
      }
    }

    e.target.value = "";
  };
  
  const removeImageAt = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const saveRule = async (e) => {
    e.preventDefault();
    setSavingRule(true);
    try {
      const cleanBody = form.body.trim() ? form.body.trim() : "—";
      const cleanTitle = form.title.trim() ? form.title.trim() : "قاعدة";

      const payload = {
        title: cleanTitle,
        body: cleanBody,
        order: Number(form.order || 1),
        images: form.images,
      };

      if (editingRule) {
        await api.put(`/leagues/${id}/rules/${editingRule}`, payload);
        toast.success("تم تحديث القاعدة");
      } else {
        await api.post(`/leagues/${id}/rules`, payload);
        toast.success("تمت إضافة القاعدة");
      }
      cancelRuleForm();
      await loadRules();
      await loadLeagueDetails();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (ruleId) => {
 
    if (!confirm("حذف القاعدة؟")) return;
    try {
      await api.delete(`/leagues/${id}/rules/${ruleId}`);
      toast.success("تم حذف القاعدة");
      await loadRules();
      await loadLeagueDetails();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const deleteLeague = async () => {
    if (!league?.id) return;
    if (!confirm(`حذف الدوري "${league.name}" نهائياً؟`)) return;
    try {
      await api.delete(`/leagues/${league.id}`);
      toast.success("تم حذف الدوري");
      navigate("/leagues");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white flex items-center gap-3">
            <Trophy className="text-gold-500 shrink-0" size={32} /> {league?.name || "تفاصيل الدوري"}
          </h1>
          {league?.game && <p className="text-white/50 text-sm mt-1">{league.game}</p>}
        </div>
        <div className="flex items-center gap-2">
          {isOwner && league?.is_custom && (
            <button
              onClick={deleteLeague}
              className="px-3.5 py-2 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs hover:bg-destructive/20 font-bold inline-flex items-center gap-1.5 transition"
              data-testid="league-delete-btn"
              title="حذف الدوري"
            >
              <Trash2 size={14} /> حذف الدوري
            </button>
          )}
          <Link
            to="/leagues"
            className="text-xs font-bold text-white/60 hover:text-gold-400 inline-flex items-center gap-1 bg-white/5 border border-white/10 px-3.5 py-2 rounded-xl transition"
          >
            <ChevronRight size={14} /> العودة للدوريات
          </Link>
        </div>
      </div>

      {/* ── Tabs Selector ── */}
      <div className="flex justify-start">
        <div className="inline-flex items-center bg-[#111113]/90 border border-white/10 rounded-2xl p-1.5 shadow-xl">
          <button
            onClick={() => setActiveTab("standings")}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
              activeTab === "standings"
                ? "bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]"
                : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
            data-testid="league-tab-standings"
          >
            <ListOrdered size={15} /> ترتيب الكلانات
          </button>
          <button
            onClick={() => setActiveTab("rules")}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
              activeTab === "rules"
                ? "bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]"
                : "text-white/50 hover:text-white hover:bg-white/5"
            }`}
            data-testid="league-tab-rules"
          >
            <ScrollText size={15} /> القوانين والشروط
          </button>
        </div>
      </div>

      {loading && (
        <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-12 text-center text-white/50 flex items-center justify-center gap-2 text-xs">
          <Loader2 className="h-4 w-4 animate-spin text-gold-500" /> جاري تحميل بيانات الدوري...
        </div>
      )}

      {!loading && error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-2xl p-4 text-destructive text-xs">
          {error}
        </div>
      )}

      {!loading && !error && activeTab === "standings" && (
        <section className="bg-[#111113]/90 border border-white/10 rounded-3xl overflow-hidden shadow-2xl" data-testid="league-detail-standings">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="font-display font-black text-lg text-white">جدول ترتيب الكلانات</h2>
              <p className="text-xs text-white/45 mt-0.5">أول 4 مراكز تأهلت تلقائياً لمواجهات بطولة السوبر رايفلز 🔥</p>
            </div>
          </div>

          {standings.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-xs">لا توجد بيانات ترتيب مخصصة حتى الآن.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm" data-testid="league-standings-table">
                <thead className="bg-white/5 text-[10px] uppercase font-bold tracking-widest text-white/40 border-b border-white/5">
                  <tr>
                    <th className="py-3.5 px-6 w-16 text-center">#</th>
                    <th className="py-3.5 px-6">الكلان</th>
                    <th className="py-3.5 px-6 w-28 text-center">فوز / خسارة</th>
                    <th className="py-3.5 px-6 w-24 text-end">النقاط</th>
                    <th className="py-3.5 px-6 w-48 text-end">الحالة / التأهل</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {standings.map((r, idx) => {
                    const qualified = idx < QUALIFY_TOP;
                    return (
                      <tr
                        key={r.clan_id}
                        data-testid={`league-standings-row-${r.clan_id}`}
                        className={`transition-colors hover:bg-white/[0.02] ${qualified ? "bg-emerald-500/5" : ""}`}
                      >
                        <td className="py-4 px-6 text-center">
                          {idx === 0 ? (
                            <span className="inline-grid place-items-center h-8 w-8 rounded-xl bg-gradient-to-b from-gold-300 via-gold-500 to-gold-700 text-black shadow-[0_0_12px_rgba(255,204,0,0.4)]">
                              <Crown size={15} />
                            </span>
                          ) : idx === 1 ? (
                            <span className="inline-grid place-items-center h-8 w-8 rounded-xl bg-gradient-to-b from-gray-200 via-gray-400 to-gray-600 text-black font-display font-black text-xs shadow-sm">
                              2
                            </span>
                          ) : idx === 2 ? (
                            <span className="inline-grid place-items-center h-8 w-8 rounded-xl bg-gradient-to-b from-amber-600 via-amber-700 to-amber-900 text-white font-display font-black text-xs shadow-sm">
                              3
                            </span>
                          ) : (
                            <span className={`text-xs font-bold ${qualified ? "text-emerald-400" : "text-white/40"}`}>{idx + 1}</span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <Link
                            to={`/clans/${r.clan_id}`}
                            className="inline-flex items-center gap-3 group"
                          >
                            <ClanLogo
                              clan={{ logo: r.logo || r.logo_url || r.avatar }}
                              className="h-9 w-9 rounded-xl border border-gold-500/20 bg-gold-500/10 grid place-items-center text-gold-500 overflow-hidden shrink-0 shadow-sm"
                              fallbackIconSize={16}
                            />
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-white text-sm group-hover:text-gold-400 transition">{r.clan_name}</span>
                              <span className="text-xs font-mono text-white/40">[{r.clan_tag}]</span>
                            </div>
                          </Link>
                        </td>
                        <td className="py-4 px-6 text-center text-xs">
                          <span className="text-emerald-400 font-bold">{r.wins}</span>
                          <span className="text-white/20 mx-1.5">/</span>
                          <span className="text-rose-400 font-bold">{r.losses}</span>
                        </td>
                        <td className={`py-4 px-6 text-end font-display font-black text-base ${qualified ? "text-emerald-400" : "text-gold-500"}`}>{r.points || 0}</td>
                        <td className="py-4 px-6 text-end">
                          {qualified ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-black tracking-wider text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-1 rounded-lg shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                              مؤهل لسوبر رايفلز 🟢
                            </span>
                          ) : (
                            <span className="text-xs text-white/30 font-medium">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {!loading && !error && activeTab === "rules" && (
        <section className="bg-[#111113]/90 border border-white/10 rounded-3xl overflow-hidden shadow-2xl" data-testid="league-detail-rules">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
            <h2 className="font-display font-black text-lg text-white">قوانين وشروط الدوري</h2>
            {isStaff && (
              <button
                onClick={startCreateRule}
                className="px-3.5 py-2 rounded-xl bg-gold-500 text-black text-xs font-black hover:bg-gold-400 inline-flex items-center gap-1 shadow-md transition"
                data-testid="league-add-rule-btn"
              >
                <Plus size={15} /> إضافة قاعدة جديدة
              </button>
            )}
          </div>
          <div className="p-6 space-y-6">
            {(creatingRule || editingRule) && isStaff && (
              <form onSubmit={saveRule} className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3.5 shadow-xl" data-testid="league-rule-form">
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="عنوان القاعدة"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-gold-500/50 text-white text-sm font-bold"
                />
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                  placeholder="وصف وتفاصيل القاعدة..."
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-gold-500/50 resize-none text-white text-sm"
                />
                <div className="flex items-center gap-3">
                  <span className="text-xs text-white/50 font-bold">الترتيب:</span>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))}
                    className="w-24 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 outline-none text-white text-sm font-mono text-center"
                  />
                </div>
                
                {/* خيار إدخال رابط صورة مباشر */}
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    placeholder="ألصق رابط صورة مباشر (https://.../image.png)"
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3.5 py-2 outline-none focus:border-gold-500/50 text-white text-xs"
                  />
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="px-3.5 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs text-white font-bold inline-flex items-center gap-1 transition"
                  >
                    <LinkIcon size={13} /> إضافة الرابط
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer text-xs text-white font-bold transition">
                    <Plus size={14} /> رفع صورة من الجهاز
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onPickImages} />
                  </label>
                </div>

                {form.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
                    {form.images.map((img, idx) => (
                      <div key={`form-rule-img-${idx}`} className="relative rounded-xl border border-white/10 overflow-hidden bg-black/40">
                        <img src={img} alt={`rule-${idx}`} className="h-24 w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImageAt(idx)}
                          className="absolute top-1 left-1 h-6 w-6 rounded-lg bg-black/80 text-white grid place-items-center hover:bg-destructive transition"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 justify-end pt-2">
                  <button type="submit" disabled={savingRule} className="px-5 py-2 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 inline-flex items-center gap-1 disabled:opacity-60 text-xs shadow-md transition">
                    <Save size={14} /> {savingRule ? "جاري الحفظ..." : "حفظ القاعدة"}
                  </button>
                  <button type="button" onClick={cancelRuleForm} className="px-4 py-2 rounded-xl hover:bg-white/5 inline-flex items-center gap-1 text-white/70 text-xs font-bold transition">
                    <X size={14} /> إلغاء
                  </button>
                </div>
              </form>
            )}

            {rulesLoading ? (
              <div className="text-white/40 text-xs inline-flex items-center gap-2 py-6">
                <Loader2 className="h-4 w-4 animate-spin text-gold-500" /> جاري تحميل القوانين...
              </div>
            ) : leagueRules.length > 0 ? (
              <div className="space-y-4" data-testid="league-rules-cards">
                {leagueRules.map((rule, idx) => (
                  <article key={rule.id} className="relative rounded-2xl border border-white/10 bg-black/40 p-5 overflow-hidden shadow-md">
                    <div className="absolute inset-y-0 right-0 w-1 bg-gold-500" />
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gold-500 font-display font-black text-sm">#{idx + 1}</span>
                          <h3 className="font-display font-black text-base text-white truncate">{rule.title}</h3>
                        </div>
                        <p className="text-xs text-white/70 mt-2 leading-relaxed whitespace-pre-wrap">{rule.body || "—"}</p>
                        
                        {Array.isArray(rule.images) && rule.images.length > 0 && (
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {rule.images.map((img, imageIdx) => (
                              <div
                                key={`${rule.id}-img-${imageIdx}`}
                                className="relative group cursor-pointer overflow-hidden rounded-xl border border-white/10 bg-black/30"
                                onClick={() => setSelectedImage(img)}
                              >
                                <img
                                  src={img}
                                  alt={`${rule.title}-${imageIdx + 1}`}
                                  className="w-full h-36 object-cover transition-transform duration-300 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Maximize2 className="text-white" size={20} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {isStaff && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => startEditRule(rule)} className="p-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition" title="تعديل">
                            <Edit size={15} />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} className="p-2 rounded-xl hover:bg-destructive/10 text-destructive transition" title="حذف">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-white/40 text-xs py-8 text-center bg-black/20 rounded-2xl border border-white/5">لا توجد قواعد مضافة لهذا الدوري حالياً.</div>
            )}

            {rulesImages.length > 0 && (
              <div className="border-t border-white/10 pt-4 mt-6">
                <div className="text-xs font-bold text-white/60 mb-3">معرض صور القوانين الإضافية:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {rulesImages.map((img, idx) => (
                    <div
                      key={`league-rule-image-${idx}`}
                      className="relative group cursor-pointer overflow-hidden rounded-xl border border-white/10 bg-black/30"
                      onClick={() => setSelectedImage(img)}
                    >
                      <img
                        src={img}
                        alt={`صورة قوانين الدوري ${idx + 1}`}
                        className="w-full h-36 object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Maximize2 className="text-white" size={20} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* نافذة تكبير الصور (Modal Lightbox) */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-black/50 rounded-full transition-colors"
              title="إغلاق"
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage}
              alt="صورة مكبرة"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeagueDetailPage() {
  return <LeagueDetailPageLegacy />;
}
